import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Dialog, DialogContent, EmptyState, ErrorState, Icon, Input, Skeleton, toast } from "@sagot/ui";
import { useState } from "react";
import { useDebounced } from "../../../lib/slug";
import { useErrorMessage } from "../../../lib/api-errors";
import { SEARCH_DEBOUNCE_MS, replyCapOf, type GlossaryTerm } from "./glossary-model";
import { GlossaryTermDialog } from "./glossary-term-dialog";
import { ReplyCapCard } from "./reply-cap-card";
import { SettingsCard } from "./settings-ui";
import { useGlossaryMutations, useGlossaryTerms } from "./use-glossary";

const SKELETON_ROWS = 4;

interface GlossaryTabProps {
  agentId: string;
  behavior: Record<string, unknown> | undefined;
  /** May change the agent (`agents.edit`). */
  canEdit: boolean;
}

function TermRow({ term, canEdit, onEdit, onRemove }: { term: GlossaryTerm; canEdit: boolean; onEdit: () => void; onRemove: () => void }) {
  const { t } = useTranslation();
  return (
    <li className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start sm:justify-between" data-testid="glossary-term">
      <div className="min-w-0 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="break-words text-sm font-semibold">{term.term}</span>
          <Badge>{t(`glossary.form.localeOptions.${term.locale}`)}</Badge>
          {term.protected ? <Badge tone="brand"><Icon name="lock" className="size-3" />{t("glossary.flags.protectedBadge")}</Badge> : null}
          {term.escalation ? <Badge tone="warning"><Icon name="headset" className="size-3" />{t("glossary.flags.escalationBadge")}</Badge> : null}
        </div>
        {term.synonyms.length > 0 ? (
          <p className="break-words text-xs text-fg-muted">
            <span className="font-semibold">{t("glossary.tab.synonyms")}: </span>
            {term.synonyms.map((s) => s.text).join(", ")}
          </p>
        ) : null}
        <p className="text-xs text-fg-subtle">
          {term.agent_id ? t("glossary.tab.scopeThis") : t("glossary.tab.scopeAll")}
          {" · "}
          {term.source === "pack" && term.pack_key ? t("glossary.tab.sourcePack", { pack: term.pack_key }) : t("glossary.tab.sourceOperator")}
        </p>
      </div>
      {canEdit ? (
        <div className="flex shrink-0 gap-1">
          <Button type="button" variant="ghost" iconOnly aria-label={t("glossary.tab.edit", { term: term.term })} onClick={onEdit}><Icon name="pencil" /></Button>
          <Button type="button" variant="ghost" iconOnly aria-label={t("glossary.tab.remove", { term: term.term })} onClick={onRemove}><Icon name="trash" /></Button>
        </div>
      ) : null}
    </li>
  );
}

function ListSkeleton() {
  return (
    <ul aria-hidden="true" className="divide-y divide-line">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li key={i} className="space-y-2 px-5 py-4"><Skeleton className="h-4 w-48" /><Skeleton className="h-3 w-72 max-w-full" /></li>
      ))}
    </ul>
  );
}

function RemoveDialog({ term, agentId, onClose }: { term: GlossaryTerm; agentId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { remove } = useGlossaryMutations(agentId);
  const confirm = () => {
    remove.mutate(term.id, {
      onSuccess: () => { toast.success(t("glossary.tab.removed")); onClose(); },
      onError: (error) => { toast.error(message(error)); },
    });
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t("glossary.tab.deleteTitle", { term: term.term })}
        description={t("glossary.tab.deleteBody")}
        closeLabel={t("common.close")}
        className="w-full max-w-md"
        footer={
          <>
            <Button type="button" variant="ghost" onClick={onClose}>{t("glossary.tab.cancel")}</Button>
            <Button type="button" variant="danger" loading={remove.isPending} onClick={confirm}>{t("glossary.tab.deleteConfirm")}</Button>
          </>
        }
      />
    </Dialog>
  );
}

/** Agent Settings > Glossary: the workspace's words as this agent reads them, plus the agent's reply-length cap. */
export function GlossaryTab({ agentId, behavior, canEdit }: GlossaryTabProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const query = useDebounced(search, SEARCH_DEBOUNCE_MS);
  const terms = useGlossaryTerms(agentId, query);
  const [editing, setEditing] = useState<GlossaryTerm | "new" | null>(null);
  const [removing, setRemoving] = useState<GlossaryTerm | null>(null);
  const items = terms.data?.pages.flatMap((page) => page.items) ?? [];
  const searching = query.trim().length > 0;

  const addButton = canEdit ? (
    <Button type="button" onClick={() => { setEditing("new"); }}><Icon name="plus" />{t("glossary.tab.add")}</Button>
  ) : null;

  let body;
  if (terms.isPending) body = <ListSkeleton />;
  else if (terms.isError) {
    body = <div className="p-5"><ErrorState title={t("glossary.tab.loadError")} description={t("glossary.tab.loadErrorBody")} onRetry={() => void terms.refetch()} retryLabel={t("common.retry")} /></div>;
  } else if (items.length === 0 && searching) {
    body = (
      <EmptyState
        icon="search"
        title={t("glossary.empty.noMatchTitle", { query: query.trim() })}
        description={t("glossary.empty.noMatchBody")}
        action={<Button type="button" variant="secondary" onClick={() => { setSearch(""); }}>{t("glossary.empty.clear")}</Button>}
      />
    );
  } else if (items.length === 0) {
    body = <EmptyState icon="languages" title={t("glossary.empty.title")} description={t("glossary.empty.body")} action={addButton ? <Button type="button" onClick={() => { setEditing("new"); }}><Icon name="plus" />{t("glossary.empty.cta")}</Button> : undefined} />;
  } else {
    body = (
      <>
        <ul aria-label={t("glossary.tab.listLabel")} className="divide-y divide-line">
          {items.map((term) => <TermRow key={term.id} term={term} canEdit={canEdit} onEdit={() => { setEditing(term); }} onRemove={() => { setRemoving(term); }} />)}
        </ul>
        {terms.hasNextPage ? (
          <div className="border-t border-line p-4 text-center">
            <Button type="button" variant="secondary" loading={terms.isFetchingNextPage} onClick={() => void terms.fetchNextPage()}>{t("glossary.tab.loadMore")}</Button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <>
      <SettingsCard title={t("glossary.tab.title")} hint={t("glossary.tab.subtitle")} aside={addButton} flush>
        <div className="flex flex-col gap-2 border-b border-line px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative sm:w-80">
            <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle" />
            <Input
              type="search"
              className="pl-9"
              aria-label={t("glossary.tab.searchLabel")}
              placeholder={t("glossary.tab.searchPlaceholder")}
              value={search}
              onChange={(event) => { setSearch(event.target.value); }}
              onKeyDown={(event) => { if (event.key === "Enter") event.preventDefault(); }}
            />
          </div>
          {terms.isSuccess ? <p className="text-xs text-fg-muted" role="status">{t("glossary.tab.count", { count: items.length })}{terms.hasNextPage ? "+" : ""}</p> : null}
        </div>
        {body}
      </SettingsCard>
      <ReplyCapCard agentId={agentId} cap={replyCapOf(behavior)} canEdit={canEdit} />
      {editing ? (
        <GlossaryTermDialog
          key={editing === "new" ? "new" : editing.id}
          agentId={agentId}
          editing={editing === "new" ? null : editing}
          onClose={(saved) => {
            setEditing(null);
            if (saved) toast.success(t(saved === "added" ? "glossary.tab.added" : "glossary.tab.saved"));
          }}
        />
      ) : null}
      {removing ? <RemoveDialog term={removing} agentId={agentId} onClose={() => { setRemoving(null); }} /> : null}
    </>
  );
}
